/**
 * SkillSelector — Picker für Skills + Builtins + Action-Types.
 * @since v2.514.0 (#1173)
 *
 * Props:
 *   - value: { kind: 'skill'|'builtin'|'action', skill_id?, action? }
 *   - library: { prompt_template: [], tool_call: [] }
 *   - builtins: BUILTIN_ACTIONS_CATALOG
 *   - actionTypes: ACTION_TYPES_CATALOG
 *   - onChange(value)
 */
export default function SkillSelector({ value, library = { prompt_template: [], tool_call: [] }, builtins = [], actionTypes = [], onChange }) {
  function pick(kind, payload) {
    onChange && onChange({ kind, ...payload });
  }

  const kind = value?.kind || 'skill';

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-1 text-[11px] font-medium">
        {['skill', 'builtin', 'action'].map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => pick(k, {})}
            className={`px-2 py-1 rounded ${kind === k ? 'bg-[#338edc] text-white' : 'bg-gray-100 text-[var(--text-secondary)] hover:bg-gray-200'}`}
          >
            {k === 'skill' ? 'Skill' : k === 'builtin' ? 'Builtin' : 'Action'}
          </button>
        ))}
      </div>

      {kind === 'skill' && (
        <div className="space-y-2">
          <Section label="Prompt-Template-Skills">
            {library.prompt_template.map((s) => (
              <SkillRow key={s.id} skill={s} selected={value?.skill_id === s.id} onClick={() => pick('skill', { skill_id: s.id, skill_slug: s.slug })} />
            ))}
            {library.prompt_template.length === 0 && <Empty />}
          </Section>
          <Section label="Tool-Call-Skills">
            {library.tool_call.map((s) => (
              <SkillRow key={s.id} skill={s} selected={value?.skill_id === s.id} onClick={() => pick('skill', { skill_id: s.id, skill_slug: s.slug })} />
            ))}
            {library.tool_call.length === 0 && <Empty />}
          </Section>
        </div>
      )}

      {kind === 'builtin' && (
        <div className="space-y-1">
          {builtins.map((b) => (
            <button
              key={b.action}
              type="button"
              onClick={() => pick('builtin', { action: b.action })}
              className={`w-full text-left px-2 py-1.5 rounded text-xs flex items-center gap-2 ${value?.action === b.action ? 'bg-[#338edc]/10 text-[#338edc]' : 'hover:bg-gray-50 text-[var(--text-secondary)]'}`}
            >
              <span className="font-mono">{b.action}</span>
              <span className="text-[10px] text-[var(--text-tertiary)]">[{b.group}]</span>
            </button>
          ))}
        </div>
      )}

      {kind === 'action' && (
        <div className="space-y-1">
          {actionTypes.map((a) => (
            <button
              key={a.action_type}
              type="button"
              onClick={() => pick('action', { action_type: a.action_type })}
              className={`w-full text-left px-2 py-1.5 rounded text-xs ${value?.action_type === a.action_type ? 'bg-[#338edc]/10 text-[#338edc]' : 'hover:bg-gray-50 text-[var(--text-secondary)]'}`}
            >
              <div className="font-mono font-medium">{a.action_type}</div>
              <div className="text-[10px] text-[var(--text-tertiary)] mt-0.5">{a.description}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Section({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-[var(--text-tertiary)] mb-1 font-medium">{label}</div>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}

function SkillRow({ skill, selected, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left px-2 py-1.5 rounded text-xs flex items-center gap-2 ${selected ? 'bg-[#338edc]/10 text-[#338edc]' : 'hover:bg-gray-50 text-[var(--text-secondary)]'}`}
    >
      <span className="font-medium">{skill.name}</span>
      <span className="text-[10px] text-[var(--text-tertiary)] font-mono">{skill.slug}</span>
      {skill.category && <span className="text-[10px] text-[var(--text-tertiary)] ml-auto">[{skill.category}]</span>}
    </button>
  );
}

function Empty() {
  return <div className="text-[10px] italic text-[var(--text-tertiary)] px-2 py-1">— keine —</div>;
}
